/**
 * DTO → view model for My Dashboard (HS-1438).
 *
 * Permanent: it survives the removal of `src/tmp/`, because what changes when the endpoints land
 * is where a DTO comes from, not what it means. Every defensive branch here is either a value the
 * contract allows to be null, or a value a real backend can still send wrong — an unparseable
 * date, a negative duration, a ratio above 1, an enum member added after us. The screen renders
 * either way rather than throwing inside a render.
 *
 * Locale formatting is deliberately *not* here: the adapter is pure so it stays testable without
 * an i18n instance, and a formatted string would not re-render when the user switches language.
 * That work lives in `dashboardFormat.ts`, the same split `features/sessions` uses.
 */
import type { IconName } from '@smartsash/ui';

import type {
  AccessDto,
  AccessView,
  ActiveLabSessionDto,
  ActiveLabSessionResponseDto,
  ActiveSessionView,
  DashboardSessionView,
  LivePreview,
  LivePreviewDto,
  MetricDto,
  MetricUnit,
  NotificationDto,
  NotificationsDto,
  NotificationsView,
  NotificationView,
  PeriodMetricView,
  PeriodSummaryView,
  SashState,
  SessionPresence,
  SessionSummaryDto,
} from './dashboard.types';

const MS_PER_SECOND = 1000;
const SECONDS_PER_DAY = 86_400;
const MIN_RATIO = 0;
const MAX_RATIO = 1;

function toDate(iso: string | null): Date | null {
  if (iso === null) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** An empty string is the same absence as `null`, and renders the same way. */
function toOptionalText(value: string | null): string | null {
  return value === null || value.trim() === '' ? null : value;
}

/* ------------------------------------------------------------------ *
 * Active lab session
 * ------------------------------------------------------------------ */

const KNOWN_PRESENCES: SessionPresence[] = ['active', 'away'];
const KNOWN_SASH_STATES: SashState[] = ['closed', 'open', 'unknown'];

function toPresence(value: string): SessionPresence {
  return KNOWN_PRESENCES.find((presence) => presence === value) ?? 'unknown';
}

/**
 * An unrecognised sash state is `unknown`, never `closed`. Defaulting to `closed` would let the
 * end-session dialog tell a user their sash was safe when the backend never said so.
 */
function toSashState(value: string): SashState {
  return KNOWN_SASH_STATES.find((state) => state === value) ?? 'unknown';
}

function toLivePreview(dto: LivePreviewDto | null): LivePreview | null {
  if (dto === null) return null;
  return {
    streamId: dto.streamId,
    thumbnailUrl: toOptionalText(dto.thumbnailUrl),
    capturedAt: toDate(dto.capturedAt),
    canJoin: dto.canJoin,
  };
}

/**
 * `null` when `startedAt` is missing or unparseable: a session with no origin cannot drive a
 * timer, and rendering the idle header is a better answer than rendering `NaN:NaN:NaN`.
 */
export function toActiveSessionView(dto: ActiveLabSessionDto): ActiveSessionView | null {
  const startedAt = toDate(dto.startedAt);
  if (startedAt === null) return null;

  return {
    sessionId: dto.sessionId,
    deviceId: dto.deviceId,
    deviceName: toOptionalText(dto.deviceName) ?? dto.deviceId,
    labName: toOptionalText(dto.labName),
    institutionId: dto.institutionId,
    startedAt,
    endsAt: toDate(dto.endsAt),
    presence: toPresence(dto.presence),
    sashState: toSashState(dto.sashState),
    // Only meaningful while the sash is open; a stale timestamp beside a closed sash would
    // print "open since 09:14" under a header that says the sash is closed.
    sashOpenSince: toSashState(dto.sashState) === 'open' ? toDate(dto.sashOpenSince) : null,
    livePreview: toLivePreview(dto.livePreview),
  };
}

/**
 * Folds the response into the header's world, including the clock correction.
 *
 * `now` is injected so the skew is testable; callers pass nothing and get `Date.now()`.
 * An unparseable `serverTime` yields a zero skew — trusting the browser clock is worse than
 * nothing only when the server's answer is worse still, and it never is once it parses.
 */
export function toDashboardSessionView(
  dto: ActiveLabSessionResponseDto,
  now: number = Date.now(),
): DashboardSessionView {
  const serverTime = toDate(dto.serverTime);

  return {
    session: dto.session === null ? null : toActiveSessionView(dto.session),
    skewMs: serverTime === null ? 0 : serverTime.getTime() - now,
  };
}

/* ------------------------------------------------------------------ *
 * Period summary
 * ------------------------------------------------------------------ */

interface MetricSpec {
  id: keyof SessionSummaryDto['metrics'];
  icon: IconName;
  labelKey: string;
  unit: MetricUnit;
}

/**
 * The four tiles, in the design's reading order. One table, so the icon, the label and the way
 * the number is read can never drift apart across the card, its tests and its fixture.
 *
 * Exported because the card renders these tiles *before* any data arrives: switching the period
 * must swap the four numbers, not the whole panel.
 */
export const METRIC_SPECS: MetricSpec[] = [
  { id: 'sessions', icon: 'clock-check', labelKey: 'dashboard.period.sessions', unit: 'count' },
  {
    id: 'cleanRate',
    icon: 'bar-chart-square-02',
    labelKey: 'dashboard.period.cleanRate',
    unit: 'ratio',
  },
  {
    id: 'avgDuration',
    icon: 'hourglass-03',
    labelKey: 'dashboard.period.avgDuration',
    unit: 'duration',
  },
  {
    id: 'totalTime',
    icon: 'hourglass-03',
    labelKey: 'dashboard.period.totalTime',
    unit: 'duration',
  },
];

/**
 * The four tiles with nothing in them yet — the shape the card renders while a period is
 * loading, so the labels, the icons and the grid stay mounted and only the values change.
 */
export function placeholderPeriodMetrics(): PeriodMetricView[] {
  return METRIC_SPECS.map((spec) => ({
    id: spec.id,
    icon: spec.icon,
    labelKey: spec.labelKey,
    unit: spec.unit,
    value: null,
    delta: null,
  }));
}

function toMetricValue(value: number | null, unit: MetricUnit): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  if (unit === 'ratio') return Math.min(MAX_RATIO, Math.max(MIN_RATIO, value));
  // Counts and durations are whole and never negative; a negative one is a backend bug the
  // screen should survive rather than display.
  return Math.max(0, Math.round(value));
}

/**
 * A delta is only meaningful when both sides are. It is a raw difference in the metric's own
 * unit — never a percentage, and never `Infinity` against an empty previous period.
 */
function toMetricDelta(dto: MetricDto, unit: MetricUnit): number | null {
  if (dto.previous === null || dto.value === null) return null;
  if (dto.delta !== null && Number.isFinite(dto.delta)) {
    return unit === 'ratio' ? dto.delta : Math.round(dto.delta);
  }
  // The contract asks the backend to compute it; recomputing is the fallback for an older build
  // that sends `previous` without `delta`, not a second source of truth.
  const derived = dto.value - dto.previous;
  return unit === 'ratio' ? derived : Math.round(derived);
}

export function toPeriodSummaryView(dto: SessionSummaryDto): PeriodSummaryView {
  const metrics: PeriodMetricView[] = METRIC_SPECS.map((spec) => {
    const metric = dto.metrics[spec.id];
    return {
      id: spec.id,
      icon: spec.icon,
      labelKey: spec.labelKey,
      unit: spec.unit,
      value: toMetricValue(metric.value, spec.unit),
      delta: toMetricDelta(metric, spec.unit),
    };
  });

  const completed = Math.max(0, Math.round(dto.sessionsCompleted));
  const required = Math.max(0, Math.round(dto.sessionsRequired));

  return {
    period: dto.period,
    metrics,
    sessionsCompleted: completed,
    sessionsRequired: required,
    // A `required` of 0 means the product turned the gate off; it must not read as "limited".
    isLimited: required > 0 && completed < required,
  };
}

/* ------------------------------------------------------------------ *
 * Access
 * ------------------------------------------------------------------ */

/**
 * Whole days until access lapses, rounded up so "expires in 14 hours" reads as 1 day and not 0.
 * `null` when there is no expiry, when the date is unparseable, or when it has already passed —
 * a countdown of `-3` is not a countdown, and the `suspended` state covers that case.
 */
export function daysUntilExpiry(expiresAt: Date | null, now: number): number | null {
  if (expiresAt === null) return null;
  const seconds = (expiresAt.getTime() - now) / MS_PER_SECOND;
  return seconds <= 0 ? null : Math.ceil(seconds / SECONDS_PER_DAY);
}

export function toAccessView(dto: AccessDto, now: number = Date.now()): AccessView {
  const expiresAt = toDate(dto.expiresAt);

  return {
    state: dto.state,
    expiresAt,
    // Only meaningful alongside a suspension; a reason under an active account is noise.
    suspendedReason: dto.state === 'suspended' ? dto.suspendedReason : null,
    // A pending request already speaks for the user; offering the button again invites five of them.
    canRequestExtension: dto.canRequestExtension && dto.extensionRequest.status !== 'pending',
    extensionStatus: dto.extensionRequest.status,
    daysUntilExpiry: daysUntilExpiry(expiresAt, now),
  };
}

/* ------------------------------------------------------------------ *
 * Notifications
 * ------------------------------------------------------------------ */

function toNotificationView(dto: NotificationDto): NotificationView {
  return {
    id: dto.id,
    title: dto.title,
    body: dto.body,
    severity: dto.severity,
    delivery: dto.delivery,
    requiresAcknowledgement: dto.requiresAcknowledgement,
    createdAt: toDate(dto.createdAt),
    document: dto.document,
  };
}

/** Oldest first, so a queue of toasts is read in the order it arrived. */
function byCreatedAtAscending(a: NotificationView, b: NotificationView): number {
  return (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0);
}

/**
 * Splits the one feed into the two surfaces the design draws.
 *
 * A message that has been dismissed, or acknowledged when it demanded acknowledgement, is done:
 * it stays in My Notifications but never re-interrupts. `silent` never surfaces here at all.
 * When a modal and a toast are both pending the modal wins — the toast is still returned, and
 * the page shows it once the modal closes.
 */
export function toNotificationsView(dto: NotificationsDto): NotificationsView {
  const pending = dto.items
    .filter((item) => item.dismissedAt === null)
    .filter((item) => !(item.requiresAcknowledgement && item.acknowledgedAt !== null))
    .map(toNotificationView);

  const modals = pending.filter((item) => item.delivery === 'modal').sort(byCreatedAtAscending);
  const toasts = pending.filter((item) => item.delivery === 'toast').sort(byCreatedAtAscending);

  return { modal: modals[0] ?? null, toasts };
}
